import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import t from 'react-translate';
import { pick } from 'lodash';
import { css } from '@emotion/react';
import * as yup from 'yup';

// redux
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { RootState } from 'redux/schemas';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import { isRoleplayTypeApproved } from 'redux/actions/ai-roleplay-usage';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { getCurrentUser } from 'redux/selectors/users';
import { getSkillTagsFromTaggings } from 'redux/selectors/skills-feedback';

import BaseLectureComponentContext from 'lecture_pages/directives/components/base-lecture-component/context';
import { DeepPartial } from 'utility-types';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import ActivityTitle from 'shared/components/activity/activity-title';
import ActivityStatus from 'shared/components/activity/activity-status';
import { LecturePageMode, LectureComponentProps } from 'lecture_pages/components';
import LectureComponentHeaderInput from 'shared/components/lecture-component-header-input';
import LectureComponentDeadline from 'components/lecture-component-deadline';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { gray3, gray7 } from 'styles/global_defaults/colors';
import ViewValidationError from 'quizzes/components/view-validation-error';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import ActivityPoints from 'shared/components/activity/activity-points';
import { AngularContext } from 'react-app';
import ClickableContainer from 'components/clickable-container';
import { config } from '@config/config.json';
import { NotAvailableGreyContainer, NotAvailableRedContainer } from 'shared/components/activity/not-available-container';
import EditInstructionFormModal from './ai-config-modal/set-up-instructions/edit-instruction-form-modal';
import EditAiInstructionContext from './ai-config-modal/set-up-instructions/context';
import { getModalBodyStyles, sharedStyles } from './ai-config-modal/styles';
import SubmitSection from './submit-section';
import useAiRolePlaySubmissionPusherEvents from './hooks/use-ai-role-play-submission-pusher-events';

type AiRolePlayLectureComponentType = LectureComponent<ComponentType.AI_ROLE_PLAY>;
const MODAL_WIDTH = 1080;

const AiRolePlayLectureComponent = (props: LectureComponentProps<ComponentType.AI_ROLE_PLAY>) => {
  const {
    mode,
    currentLecture,
    lectureComponent,
  } = props;

  const styles = css`
    .description-container {
      width: 100%;
      text-align: left;
      .text-container {
        span {
          vertical-align: top !important;
        }
      }
      .bs4-input-group-append {
        display: none;
      }
      textarea {
        padding: ${quarterSpacing}px !important;
        background-color: ${mode === LecturePageMode.EDIT ? gray7 : 'transparent'} !important;
        line-height: ${headerLineHeight}px;
        min-height: ${mode === LecturePageMode.EDIT ? '200px' : '0px'} !important;
      }

      .bs4-form-control {
        border: ${mode === LecturePageMode.EDIT ? `1px solid ${gray3}` : 'none'} !important;
        text-align: left;
        box-sizing: border-box;
        padding: 0;
        overflow: hidden;
      }
    }
  `;

  const modalStyles = css`
    .bs4-modal-body {
      padding: 0px;
    }
  `;

  const dispatch = useAppDispatch();
  const currentLectureId = currentLecture.id;
  const { injectServices } = useContext(AngularContext);
  const [CurrentUserManager, CurrentPermissionsManager, $state] = injectServices(['CurrentUserManager', 'CurrentPermissionsManager', '$state']);

  const aiRolePlay = useSelector((state: RootState) => getAiRolePlay(state, lectureComponent.aiRolePlay));
  const { pointsConfigurationId } = aiRolePlay || {};
  const pointsConfiguration = useSelector((state: RootState) => (pointsConfigurationId ? state.models.pointsConfigurations[pointsConfigurationId] : null));
  const currentCourse = useSelector(getCurrentCourse);
  const currentCatalogId = useSelector((state: RootState) => state.app.currentCatalogId);
  const skillTags = useSelector((state: RootState) => getSkillTagsFromTaggings(state, lectureComponent.skillTaggings) ?? []);
  const institution = useSelector(getCurrentInstitution);
  const courseAliases = useSelector((state: RootState) => getFlatCourseAliases(state, currentCatalogId));
  const isStarterVoiceRolePlay = aiRolePlay?.interactionMode === RolePlayInteractionMode.VOICE
    && !institution?.practicePlusEnabled;
  // Approved until the roleplay loads, so the not-approved banner doesn't flash on mount.
  const isRolePlayTypeApproved = aiRolePlay?.interactionMode
    ? isRoleplayTypeApproved(currentCourse?.aiRolePlayAccessSettings, aiRolePlay.interactionMode)
    : true;
  const starterRoleplayAccessUsers = CurrentPermissionsManager.isCourseBuilder()
    || CurrentPermissionsManager.hasCourseAdminPermissions();
  const showStarterVoiceWarning = starterRoleplayAccessUsers && isStarterVoiceRolePlay;
  // Starter-voice warning takes precedence over the not-approved banner.
  const showNotApprovedWarning = starterRoleplayAccessUsers
    && !isRolePlayTypeApproved
    && !isStarterVoiceRolePlay;
  const showStarterVoiceUnavailable = !starterRoleplayAccessUsers
    && (isStarterVoiceRolePlay || !isRolePlayTypeApproved || !aiRolePlay?.canStartRolePlay);
  // Type approval only gates learners; builders/admins can still run an unapproved roleplay.
  const isDisabledByTypeApproval = !isRolePlayTypeApproved && !starterRoleplayAccessUsers;

  const [showEditAiInstruction, setShowEditAiInstruction] = useState(false);

  const forwardOnModalCloseRef = useRef(null);

  const isCurrentCourseSelfPaced = currentCourse.isSelfPaced;
  const lectureComponentId = lectureComponent.id;
  const {
    expirationDate,
  } = aiRolePlay || {};
  const isHardDeadlineExpired = aiRolePlay?.hardDeadline && aiRolePlay?.expired;

  const [title, setTitle] = useState<string>(aiRolePlay?.title ?? '');

  const descriptionValidationSchema = yup.object().shape({
    description: yup.string().max(10000, t.VALIDATION.MAX_LENGTH('10000')),
  });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      description: aiRolePlay?.description || '',
    },
    resolver: yupResolver(descriptionValidationSchema),
  });
  const { formState: { errors }, reset } = methods;

  // Sync form with Redux state when aiRolePlay.description changes (e.g., after update API)
  useEffect(() => {
    reset({ description: aiRolePlay?.description || '' });
  }, [aiRolePlay?.description, reset]);

  // Sync title state with Redux state when aiRolePlay.title changes (e.g., after update API)
  useEffect(() => {
    setTitle(aiRolePlay?.title || '');
  }, [aiRolePlay?.title]);

  const { sharedProps, setSharedProps } = useContext(BaseLectureComponentContext);
  const initialSharedProps = useRef(sharedProps).current;

  const lecturePageId = useSelector((state: RootState) => state.app.lecturePage.currentLectureId);
  const lecturePage = useSelector((state: RootState) => state.models.lecturePages[lecturePageId]);
  const { anonymizedIdentifier } = useSelector(getCurrentUser);
  const courseId = currentCourse.id;

  useAiRolePlaySubmissionPusherEvents({
    anonymizedIdentifier,
    courseId,
    aiRolePlayId: aiRolePlay?.id,
    courseCatalogId: currentCatalogId,
    pointsReceived: aiRolePlay?.pointsReceived,
    skillTags,
    interactionMode: aiRolePlay?.interactionMode,
  });

  const openEditAiInstruction = useCallback(() => {
    setShowEditAiInstruction(true);
  }, []);

  const saveComponent = useCallback((componentDataPatch: DeepPartial<AiRolePlayLectureComponentType['aiRolePlay']>) => {
    const componentDataUpdate = {
      catalogId: currentCatalogId,
      lecturePageId: currentLectureId,
      componentData: {
        id: lectureComponentId,
        type: lectureComponent.type,
        aiRolePlay: {
          ...pick(aiRolePlay, [
            'isTodo',
            'hardDeadline',
            'expirationDate',
          ]),
          ...componentDataPatch,
        },
      },
    };

    return dispatch(updateLectureComponent(componentDataUpdate));
  }, [
    dispatch,
    aiRolePlay,
    currentLectureId,
    currentCatalogId,
    lectureComponentId,
    lectureComponent.type,
  ]);

  useEffect(() => {
    const options: NvDropdownOption[] = [{
      type: 'text',
      callback: openEditAiInstruction,
      text: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.EDIT_DROPDOWN.EDIT_ROLE_PLAY(),
      tooltip: {
        enabled: !!lecturePage.isLinked,
        text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
        placement: 'left',
        offset: 20,
      },
    }];

    if (!isCurrentCourseSelfPaced) {
      options.unshift(
        { type: 'text',
          text: expirationDate ? t.LECTURE_PAGES.COMPONENTS.DROPDOWN.REMOVE_DEADLINE() : t.LECTURE_PAGES.COMPONENTS.DROPDOWN.ADD_DEADLINE(),
          callback: () => {
            saveComponent({
              expirationDate: expirationDate
                ? null
                : moment(currentLecture.releaseDate).add(1, 'weeks').endOf('day').toISOString(),
            });
          },
        },
        {
          type: 'divider',
        },
      );
    }

    setSharedProps({
      ...initialSharedProps,
      extraOptions: {
        mode: 'prepend',
        renderOnMount: true,
        options,
      },
    });
  }, [
    saveComponent,
    setSharedProps,
    expirationDate,
    openEditAiInstruction,
    initialSharedProps,
    isCurrentCourseSelfPaced,
    currentLecture.releaseDate,
    lecturePage.isLinked,
  ]);

  const handleTitleBlur = () => saveComponent({
    title,
  });
  const handleTitleInputChange = (newTitle: string) => setTitle(newTitle);

  const handleDescriptionBlur = (newDescription: string) => {
    if (mode !== LecturePageMode.EDIT || errors.description) {
      return;
    }
    saveComponent({
      description: newDescription,
    });
  };

  const showDescriptionMaxCharLimitError = () => errors.description?.type === 'max';

  const canViewRolePlayResultsGallery = () => (
    (CurrentUserManager.isAdmin() || CurrentPermissionsManager.isMentor())
    && mode === LecturePageMode.VIEW
    && aiRolePlay.hasCompletedSubmissions
  );

  const handleOpenRolePlay = () => {
    $state.go('role-play-submission-modal', {
      rolePlayId: aiRolePlay.id,
      section: 'new',
    });
  };

  return (
    <div
      css={styles}
      className='d-flex flex-column align-items-center pb-6'
    >
      <div className='mb-2'>
        <ActivityStatus icon='role-play' status={aiRolePlay.progress} />
      </div>
      <ActivityTitle
        status={aiRolePlay.progress}
        statusesTexts={{
          not_started: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.PRACTICE(),
          completed: t.FORM.SUBMITTED(),
        }}
      />
      <LectureComponentHeaderInput
        required
        value={title}
        editable={mode === LecturePageMode.EDIT}
        onBlur={handleTitleBlur}
        onChange={handleTitleInputChange}
      />
      {
        pointsConfiguration
          && !!pointsConfiguration.points
          && (
            <div
              className='activity-points mb-2'
            >
              <ActivityPoints
                pointsReceived={aiRolePlay.pointsReceived}
                totalPoints={pointsConfiguration.points}
              />
            </div>
          )
      }
      <div className={expirationDate ? 'mb-2' : ''}>
        <LectureComponentDeadline
          isEdit={mode === LecturePageMode.EDIT}
          catalogId={currentCatalogId}
          componentName='ai-role-play'
          lecturePageId={currentLecture.id}
          expirationDate={expirationDate}
          hasHardDeadlines={aiRolePlay?.hardDeadline}
          onHardDeadlineChange={(hardDeadline) => {
            saveComponent({ hardDeadline });
          }}
          onDeadlineChange={(date) => {
            saveComponent({ expirationDate: date?.toISOString() });
          }}
          isDeadlineEditable={mode === LecturePageMode.EDIT}
        />
      </div>
      {(aiRolePlay?.description || mode === LecturePageMode.EDIT) && (
        <FormProvider {...methods}>
          <NvTextArea
            readOnly={mode !== LecturePageMode.EDIT}
            name='description'
            className='description-container mt-4'
            withForm
            onBlur={(e) => handleDescriptionBlur(e.target.value)}
            placeholder={mode === LecturePageMode.EDIT ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.ADD_DESCRIPTION() : ''}
          />
          {showDescriptionMaxCharLimitError() && (
          <div className='d-flex justify-content-end w-100'>
            <ViewValidationError characterLimit='10,000' />
          </div>
          )}
        </FormProvider>
      )}
      {(showStarterVoiceWarning || showNotApprovedWarning) && (
        <NotAvailableRedContainer>
          <span className='text-small'>
            <span className='font-weight-bolder'>
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.STARTER_VOICE_WARNING.LABEL(courseAliases)}
            </span>
            {' '}
            {showStarterVoiceWarning
              ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.STARTER_VOICE_WARNING.BODY()
              : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.NOT_APPROVED_WARNING.BODY(courseAliases)}
          </span>
        </NotAvailableRedContainer>
      )}
      {showStarterVoiceUnavailable && !isHardDeadlineExpired ? (
        <NotAvailableGreyContainer icon='ban' text={t.LECTURE_PAGES.COMPONENTS.NOT_AVAILABLE_YET()} />
      ) : (
        <SubmitSection
          aiRolePlayId={aiRolePlay.id}
          handleOpenRolePlay={handleOpenRolePlay}
          mode={mode}
          isHardDeadlineExpired={isHardDeadlineExpired}
          isDisabledByTypeApproval={isDisabledByTypeApproval}
        />
      )}
      {canViewRolePlayResultsGallery() && (
        <div className='d-flex bg-gray-7 mt-6 py-3 justify-content-center w-100 text-small font-weight-bold'>
          <span className='text-gray-2'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.ADMIN_ONLY()}</span>
          <ClickableContainer
            className='text-primary ml-3'
            onClick={() => $state.go('role-play-submission-modal', { rolePlayId: aiRolePlay.id })}
            data-qa={config.pendo.activities.aiRolePlay.viewSubmissionGallery}
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.VIEW_ROLEPLAY_RESULTS()}
          </ClickableContainer>
        </div>
      )}
      <NvModal
        show={showEditAiInstruction}
        width={MODAL_WIDTH}
        type={ModalType.DYNAMIC}
        bodyClassName='ai-role-play-edit-modal'
        modalStyles={modalStyles}
        onClose={() => {
          if (forwardOnModalCloseRef.current) {
            forwardOnModalCloseRef.current();
          }
        }}
        header={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.EDIT_DROPDOWN.EDIT_ROLE_PLAY()}
        body={(
          <div css={[getModalBodyStyles(2), sharedStyles]}>
            <EditAiInstructionContext.Provider value={{
              showEditAiInstruction,
              setShowEditAiInstruction,
              forwardOnModalCloseRef,
              aiRolePlayId: aiRolePlay.id,
            }}
            >
              <EditInstructionFormModal
                saveComponent={saveComponent}
                lectureComponent={lectureComponent}
              />
            </EditAiInstructionContext.Provider>
          </div>

        )}
      />
    </div>
  );
};

export default AiRolePlayLectureComponent;
